"use client";

import { useDebounce } from "../hooks/common/useDebounce";
import { ChangeEvent, useEffect, useState } from "react";

interface DebouncedInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  disabled?: boolean;
  delay?: number;
  className?: string;
  rows?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  placeholder = "",
  type = "text",
  maxLength,
  disabled = false,
  delay = 500,
  className = "",
  rows = 4,
  onKeyDown,
}: DebouncedInputProps) => {
  const [value, setValue] = useState(initialValue || "");
  const debouncedValue = useDebounce(value, delay);

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  
  useEffect(() => {
    if (debouncedValue !== initialValue) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, initialValue, onChange]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const baseClassName = `w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed ${className}`;

  if (type === "textarea") {
    return (
      <textarea
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        rows={rows}
        onKeyDown={onKeyDown}
        className={`${baseClassName} resize-none`}
      />
    );
  }
  
  return (
    <input
      type={type}
      value={value || ""}
      onChange={handleChange}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
      onKeyDown={onKeyDown}
      className={`w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    />
  );
};
